import {
  useFindAllOperatingSystemsQuery,
  useFindAllSoftwareQuery,
  useFindOneSoftwareVersionQuery,
  useRemoveSoftwareVersionMutation,
} from '../../api/api-hooks';
import { Button, Grid, TextField } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import { nullStringCheck } from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import { Autocomplete } from '@mui/material';
import { SoftwareVersionForm } from './linksTypes';
import useSoftwareVersionCreator from './useSoftwareVersionCreator';
import useSoftwareVersionUpdater from './useSoftwareVersionUpdater';
import { softwareVersionValidator, versionsMiddleware } from './linksUtils';
import SoftwareTypeManagementButton from '../software-types/SoftwareTypeManagementButton';
import OperatingSystemsManagementButton from '../operating-systems/OperatingSystemsManagementButton';
import NewVersionEmailDialog from './NewVersionEmailDialog';
import { useState } from 'react';

interface Props {
  selectedVersions: Item[];
  setSelectedVersions: React.Dispatch<React.SetStateAction<Item[]>>;
}

const addFormInitialState: SoftwareVersionForm = {
  versionNumber: undefined,
  versionType: undefined,
  link: undefined,
  changelog: undefined,
  size: undefined,
  software: null,
  operatingSystem: null,
  userguideName: undefined,
  userguideLink: undefined,
};

const SoftwareVersionsOptionsBar = ({
  selectedVersions,
  setSelectedVersions,
}: Props) => {
  const [deleteVersion] = useRemoveSoftwareVersionMutation();
  const versionCreator = useSoftwareVersionCreator();
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const versionUpdater = useSoftwareVersionUpdater();
  const { data: software } = useFindAllSoftwareQuery({});
  const { data: operatingSystems } = useFindAllOperatingSystemsQuery({});
  const [dialogOpen, setDialogOpen] = useState(false);

  if (software === undefined || operatingSystems === undefined) return null;
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedVersions}
        setSelectedItems={setSelectedVersions}
        handlerMiddleware={versionsMiddleware}
        itemValidator={softwareVersionValidator}
        itemDeleter={deleteVersion}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        itemName='Software Version'
        formInstructions='Version #, Link, Size, Software, and Operating System are required.'
        addFormInitialState={addFormInitialState}
        childrenCreator={(addFormState: SoftwareVersionForm, handler: any) => (
          <Grid container spacing={4}>
            <Grid item xs={6} container direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'software');
                  }}
                  value={addFormState.software}
                  options={software}
                  getOptionLabel={(software) => software.type}
                  style={{ width: 300, marginTop: '.5rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Software'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <SoftwareTypeManagementButton />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'os');
                  }}
                  value={addFormState.operatingSystem}
                  options={operatingSystems}
                  getOptionLabel={(os) => os.osName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select OS'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <OperatingSystemsManagementButton />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='versionNumber'
                  label='Version #'
                  fullWidth
                  value={nullStringCheck(addFormState.versionNumber)}
                  onChange={(event) => {
                    handler(event);
                  }}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='versionType'
                  label='Version Type'
                  fullWidth
                  value={nullStringCheck(addFormState.versionType)}
                  onChange={handler}
                />
              </Grid>

              <Grid item>
                <TextField
                  margin='dense'
                  name='link'
                  label='Version Link'
                  fullWidth
                  value={nullStringCheck(addFormState.link)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
            <Grid item xs={6} container direction='column' spacing={1}>
              <Grid item>
                <TextField
                  margin='dense'
                  name='changelog'
                  label='Changelog Link'
                  fullWidth
                  value={nullStringCheck(addFormState.changelog)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='size'
                  label='Size (MB)'
                  fullWidth
                  value={nullStringCheck(addFormState.size)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='userguideName'
                  label='Userguide Name'
                  fullWidth
                  value={nullStringCheck(addFormState.userguideName)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='userguideLink'
                  label='Userguide Link'
                  fullWidth
                  value={nullStringCheck(addFormState.userguideLink)}
                  onChange={handler}
                />
              </Grid>
              <Grid item style={{ marginTop: '1rem' }}>
                <Button
                  variant='contained'
                  onClick={() => {
                    setDialogOpen(true);
                  }}
                  color='primary'
                  disabled={
                    !addFormState.software || !addFormState.versionNumber
                  }
                >
                  Edit and Send Notification Email
                </Button>
              </Grid>
            </Grid>
            <NewVersionEmailDialog
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              addFormState={addFormState}
            />
          </Grid>
        )}
        itemCreator={versionCreator}
        itemGetter={useFindOneSoftwareVersionQuery}
        itemUpdater={versionUpdater}
      />
    </>
  );
};

export default SoftwareVersionsOptionsBar;
